import React from 'react';
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { Lock } from "lucide-react";
import { useSidebarPermissions } from '../hooks/useSidebarPermissions';

const CategoryHeader = ({ category, isExpanded, onToggle, selectedItem }) => {
  const { isCategoryDisabled, getTooltipMessage } = useSidebarPermissions();
  
  if (!category || !category.items || !Array.isArray(category.items)) {
    return null;
  }
  
  const isCategoryDirectlySelected = category.items.length === 1 && category.items[0].name === selectedItem;
  const hasMultipleItems = category.items.length > 1;
  const isDisabled = isCategoryDisabled(category.name);
  
  const handleToggle = () => {
    if (!isDisabled) {
      onToggle();
    }
  };
  
  const buttonContent = (
    <ListItemButton
      onClick={handleToggle}
      disabled={isDisabled}
      className={`${
        isCategoryDirectlySelected ? "bg-gray-300 text-blue-800 shadow-md rounded-lg" : isDisabled ? "text-gray-400 cursor-not-allowed rounded-lg" : "text-blue-800 hover:bg-gray-100 rounded-lg"
      }`}
      sx={{
        '& .MuiListItemIcon-root': { minWidth: '24px', marginRight: '6px' },
        borderRadius: '8px',
        '&:hover': { 
          borderRadius: '8px',
          backgroundColor: isDisabled ? 'transparent' : undefined
        },
        padding: '8px 12px',
        marginBottom: '2px',
        '&.Mui-disabled': {
          opacity: 0.6
        }
      }}
    >
      <ListItemIcon>
        {category.icon && typeof category.icon === "string" ? (
          <img
            src={category.icon}
            alt={category.name}
            className={`w-4 h-4 filter brightness-0 ${isDisabled ? 'opacity-50' : ''}`}
            onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }}
          />
        ) : category.icon ? (
          <category.icon className={`w-4 h-4 ${isDisabled ? 'opacity-50' : ''} ${isCategoryDirectlySelected ? "text-blue-800" : "text-blue-800"}`} />
        ) : null}
      </ListItemIcon>
      <ListItemText 
        primary={category.name} 
        className={isCategoryDirectlySelected ? "text-blue-800" : isDisabled ? "text-gray-400" : "text-blue-800"}
        primaryTypographyProps={{ 
          fontSize: "0.9rem", 
          fontWeight: 600, 
          lineHeight: 1.2,
          textTransform: "capitalize"
        }}
      />
      {isDisabled ? (
        <Lock className="w-4 h-4 text-gray-400 ml-5" />
      ) : hasMultipleItems ? (
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ml-4 ${
            isExpanded ? "rotate-180" : ""
          } ${isCategoryDirectlySelected ? "text-blue-800" : "text-blue-800"}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      ) : null}
    </ListItemButton>
  );
  
  return (
    <ListItem disablePadding className="mb-0">
      {isDisabled && getTooltipMessage(category.name, category.name) ? (
        <Tooltip 
          title={getTooltipMessage(category.name, category.name)} 
          placement="right"
          arrow
        >
          <span>{buttonContent}</span>
        </Tooltip>
      ) : (
        buttonContent
      )}
    </ListItem>
  );
};

export default CategoryHeader; 